export interface Vec3 {
  x: number
  y: number
  z: number
}

export interface Quat {
  x: number
  y: number
  z: number
  w: number
}

/**
 * Float scratch with its own map (D-1052; P5a11b C5): a class, not a literal. `{ x, y, z }` / `{ x, y, z, w }`
 * literals share one map game-wide, so a field can turn tagged, and then every float store into it boxes a heap
 * number (16 B). Pooled per-tick scratch uses these. The fields start as numbers (the initialisers): a bare
 * `x: number` declaration defines the field as `undefined` first (class-field semantics), which makes it tagged.
 */
export class Vec3Scratch implements Vec3 {
  x = 0
  y = 0
  z = 0
  constructor(x = 0, y = 0, z = 0) {
    this.x = x
    this.y = y
    this.z = z
  }
}

/** A quaternion scratch with its own map (see `Vec3Scratch`); identity by default. */
export class QuatScratch implements Quat {
  x = 0
  y = 0
  z = 0
  w = 1
  constructor(x = 0, y = 0, z = 0, w = 1) {
    this.x = x
    this.y = y
    this.z = z
    this.w = w
  }
}

export function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v
}

// v' = v + w·t + q×t, where t = 2·(q×v). Allocation-free; out may alias the input's object.
function rotateRaw(qx: number, qy: number, qz: number, qw: number, vx: number, vy: number, vz: number, out: Vec3): Vec3 {
  const tx = 2 * (qy * vz - qz * vy)
  const ty = 2 * (qz * vx - qx * vz)
  const tz = 2 * (qx * vy - qy * vx)
  out.x = vx + qw * tx + (qy * tz - qz * ty)
  out.y = vy + qw * ty + (qz * tx - qx * tz)
  out.z = vz + qw * tz + (qx * ty - qy * tx)
  return out
}

/** Rotates a vector from the local frame of q into world space. */
export function rotateVec(q: Quat, vx: number, vy: number, vz: number, out: Vec3): Vec3 {
  return rotateRaw(q.x, q.y, q.z, q.w, vx, vy, vz, out)
}

/**
 * `rotateVec` with the vector read from `v` (`rotateRaw`'s expression, bit for bit; `out` may alias `v`). Only
 * objects cross the call, so a hot caller past V8's inlining budget boxes no floats (D-1052's `rotateInto`; P5a11b).
 */
export function rotateVecFrom(q: Readonly<Quat>, v: Readonly<Vec3>, out: Vec3): Vec3 {
  const qx = q.x
  const qy = q.y
  const qz = q.z
  const qw = q.w
  const vx = v.x
  const vy = v.y
  const vz = v.z
  const tx = 2 * (qy * vz - qz * vy)
  const ty = 2 * (qz * vx - qx * vz)
  const tz = 2 * (qx * vy - qy * vx)
  out.x = vx + qw * tx + (qy * tz - qz * ty)
  out.y = vy + qw * ty + (qz * tx - qx * tz)
  out.z = vz + qw * tz + (qx * ty - qy * tx)
  return out
}

/** The unit axes as pooled vectors for `rotateVecFrom` (never written). */
export const AXIS_X: Readonly<Vec3> = new Vec3Scratch(1, 0, 0)
export const AXIS_Y: Readonly<Vec3> = new Vec3Scratch(0, 1, 0)
export const AXIS_NEG_Z: Readonly<Vec3> = new Vec3Scratch(0, 0, -1)

/** Rotates a vector from world space into the local frame of q. */
export function inverseRotateVec(q: Quat, vx: number, vy: number, vz: number, out: Vec3): Vec3 {
  return rotateRaw(-q.x, -q.y, -q.z, q.w, vx, vy, vz, out)
}
